import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import * as forumApi from '../../services/forumApi';
import { onForumNotification } from '../../services/socket';

const FORUM_LOAD_ERROR = '__zodiacus_forum_home_load_error__';

const HOME_ZODIAC_KEY_BY_FORUM_SIGN = {
  capricornio: 'capricorn',
  acuario: 'aquarius',
  piscis: 'pisces',
  aries: 'aries',
  tauro: 'taurus',
  geminis: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  sagitario: 'sagittarius',
};

export default function ForumHome() {
  const { t } = useTranslation(['forum', 'common', 'home']);
  const [cats, setCats] = useState([]);
  const [err, setErr] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [hoverBack, setHoverBack] = useState(false);
  const [hoverItem, setHoverItem] = useState(null);

  // --- Realtime notifs (sesión)
  const [forumNotifs, setForumNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [notifsLoading, setNotifsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await forumApi.listCategories();
        setCats(data);
      } catch (e) {
        setErr(e.message || FORUM_LOAD_ERROR);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setNotifsLoading(true);
        const data = await forumApi.getForumNotifications({ limit: 12 });
        if (data && data.ok) {
          setForumNotifs(Array.isArray(data.items) ? data.items : []);
          setUnreadNotifs(typeof data.unreadCount === 'number' ? data.unreadCount : 0);
        }
      } catch {
        // best-effort
      } finally {
        setNotifsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const HOME_RETURN_BG = 'radial-gradient(circle at 48% -10%, rgba(255,215,0,.18), transparent 25%), radial-gradient(circle at 14% 17%, rgba(157,66,255,.23), transparent 34%), radial-gradient(circle at 85% 35%, rgba(218,165,32,.13), transparent 30%), radial-gradient(circle at 50% 74%, rgba(91,0,145,.28), transparent 44%), linear-gradient(180deg, #070013 0%, #15002d 38%, #090016 100%)';

    return () => {
      const viewportHeight = Math.max(
        window.visualViewport?.height || 0,
        window.innerHeight || 0,
        document.documentElement.clientHeight || 0
      );

      if (viewportHeight > 0) {
        document.documentElement.style.setProperty('--zdx-home-vh', `${Math.round(viewportHeight)}px`);
      }

      document.documentElement.style.background = HOME_RETURN_BG;
      document.documentElement.style.backgroundColor = '#090016';
      document.body.style.background = HOME_RETURN_BG;
      document.body.style.backgroundColor = '#090016';
    };
  }, []);

  const shouldOpenNotifs = !!(location && location.state && location.state.openNotifs);

  useEffect(() => {
    if (!shouldOpenNotifs) return;

    setShowNotifs(true);

    (async () => {
      try {
        const r = await forumApi.markForumNotificationsRead({ all: true });
        if (r && r.ok) {
          setUnreadNotifs(typeof r.unreadCount === 'number' ? r.unreadCount : 0);
        } else {
          setUnreadNotifs(0);
        }
      } catch {
        setUnreadNotifs(0);
      }

      try {
        setForumNotifs(prev => (Array.isArray(prev) ? prev.map(x => ({ ...x, read: true })) : prev));
      } catch {}

      // limpiar state para que no se reabra al volver atrás
      try {
        navigate('.', { replace: true, state: {} });
      } catch {}
    })();
  }, [shouldOpenNotifs, navigate]);

  const lastNotifs = useMemo(() => forumNotifs.slice(0, 12), [forumNotifs]);

  useEffect(() => {
    // Realtime: likes/respuestas del foro
    const off = onForumNotification((n) => {
      const notif = {
        id: String(n?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`),
        type: n?.type || 'forum',
        message: n?.message || t('home.notifications.fallbackMessage'),
        threadId: n?.threadId || null,
        postId: n?.postId || null,
        fromName: n?.fromName || null,
        createdAt: n?.createdAt || new Date().toISOString(),
        read: false,
      };

      setForumNotifs(prev => [notif, ...prev].slice(0, 50));
      setUnreadNotifs(prev => Math.min(99, (prev || 0) + 1));

      // Si el panel está abierto, no acumular como no leído
      setShowNotifs(currentShow => {
        if (currentShow) {
          setUnreadNotifs(0);
          setForumNotifs(prev => prev.map(x => ({ ...x, read: true })));
          return currentShow;
        }
        return currentShow;
      });
    });

    return () => { try { off && off(); } catch {} };
  }, [t]);

  useEffect(() => {
    // Cerrar panel al clickear fuera
    const onDown = (e) => {
      const t = e?.target;
      if (!t) return;
      if (t.closest && (t.closest('#forumNotifsBtn') || t.closest('#forumNotifsPanel'))) return;
      setShowNotifs(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('touchstart', onDown);
    };
  }, []);

  return (
    <HelmetProvider>
     <style>{`
        @keyframes forum-star-drift {
          from { background-position: 18px 12px, 92px 44px, center; }
          to { background-position: 18px 132px, 92px 224px, center; }
        }

        @keyframes forum-wheel-spin {
          from { rotate: 0deg; }
          to { rotate: 360deg; }
        }

        @keyframes forum-glow-pulse {
          0%, 100% { opacity: 0.52; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.82; transform: translateX(-50%) scale(1.018); }
        }

        @keyframes forum-tile-shine {
          0% { transform: translateX(-135%) rotate(12deg); opacity: 0; }
          45% { opacity: 0.45; }
          100% { transform: translateX(135%) rotate(12deg); opacity: 0; }
        }

        #forumHomeRoot::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.72) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.42) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.54));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 18px 12px, 92px 44px, center;
          opacity: 0.48;
          animation: forum-star-drift 18s linear infinite;
        }

        #forumHomeRoot::after {
          content: "";
          position: absolute;
          top: -18%;
          left: 50%;
          width: min(88vw, 780px);
          height: min(88vw, 780px);
          z-index: 0;
          pointer-events: none;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(174,65,255,0.11) 0%, rgba(218,165,32,0.055) 34%, transparent 68%);
          animation: forum-glow-pulse 6s ease-in-out infinite;
        }

        #forumHomeRoot .forumWheel {
          position: absolute;
          top: 50%;
          left: 50%;
          width: min(92vmin, 820px);
          height: min(92vmin, 820px);
          translate: -50% -50%;
          border-radius: 50%;
          background-image: url("/logo/Zodiacus.jpeg");
          background-repeat: no-repeat;
          background-position: center center;
          background-size: contain;
          opacity: 0.20;
          filter: brightness(1.16) saturate(1.08) drop-shadow(0 0 44px rgba(154,47,255,0.20));
          z-index: 0;
          pointer-events: none;
          will-change: transform;
          transform-origin: 50% 50%;
          animation: forum-wheel-spin 180s linear infinite;
          -webkit-mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%);
          mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%);
        }

        .forum-card::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 10%, rgba(218,165,32,0.045), transparent 24%),
            radial-gradient(circle at 88% 62%, rgba(188,69,255,0.065), transparent 34%);
          z-index: 0;
        }

        .forum-card > * {
          position: relative;
          z-index: 2;
        }

        .forum-sign-item {
          -webkit-tap-highlight-color: transparent;
        }

        .forum-sign-item::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background:
            radial-gradient(circle at 22% 20%, rgba(255,216,118,0.12), transparent 22%),
            radial-gradient(circle at 80% 82%, rgba(188,69,255,0.16), transparent 34%);
          opacity: 0.78;
        }

        .forum-sign-item::after {
          content: "";
          position: absolute;
          top: -24%;
          bottom: -24%;
          left: 0;
          width: 42%;
          pointer-events: none;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
          animation: forum-tile-shine 5.8s ease-in-out infinite;
        }

        .forum-sign-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 38px rgba(0,0,0,0.34), 0 0 28px rgba(184,63,255,0.22);
          border-color: rgba(232,95,255,0.72) !important;
        }

        .forum-sign-item:active {
          transform: scale(0.985);
        }

        .forum-back-btn, .forum-notifs-btn {
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          user-select: none;
        }

        .forum-back-btn:active, .forum-notifs-btn:active {
          transform: translateZ(0) scale(0.97);
          filter: brightness(0.98);
        }

        @media (max-width: 720px) {
          .forum-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }

        @media (max-width: 480px) {
          #forumHomeRoot {
            align-items: flex-start !important;
            justify-content: center !important;
            padding: 72px 16px 14px !important;
            min-height: 100svh !important;
          }

          #forumHomeRoot .forumWheel {
            top: 50% !important;
            left: 50% !important;
            width: min(94vw, 80svh) !important;
            height: min(94vw, 80svh) !important;
            opacity: 0.24 !important;
            translate: -50% -50% !important;
            transform-origin: 50% 50% !important;
            -webkit-mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%) !important;
            mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%) !important;
          }

          .forum-card {
            width: 100% !important;
            min-height: calc(100svh - 92px) !important;
            padding: 18px 18px 22px !important;
            border-radius: 26px !important;
            max-width: 650px !important;
            display: flex !important;
            flex-direction: column !important;
            transform: none !important;
          }

          .forum-header {
            min-height: 42px !important;
            margin-bottom: 10px !important;
            padding: 0 !important;
            border-bottom: none !important;
          }

          .forum-title {
            font-size: clamp(1.75rem, 9vw, 2.35rem) !important;
            line-height: 0.96 !important;
            letter-spacing: -0.04em !important;
          }

          .forum-subtitle {
            font-size: 0.84rem !important;
            line-height: 1.22 !important;
            margin: 8px auto 12px !important;
            max-width: 92% !important;
          }

          .forum-grid {
            margin-top: 10px !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            grid-auto-rows: minmax(82px, 1fr) !important;
            gap: 10px !important;
            flex: 1 !important;
            align-content: stretch !important;
          }

          .forum-sign-item {
            min-height: 0 !important;
            height: 100% !important;
            padding: 8px 9px 17px !important;
            border-radius: 16px !important;
            grid-template-columns: 1fr !important;
            grid-template-rows: auto auto !important;
            grid-template-areas: "badge" "name" !important;
            gap: 4px !important;
            justify-items: center !important;
            text-align: center !important;
          }

          .forum-sign-item::after {
            display: none !important;
          }

          .forum-badge-wrap {
            justify-self: center !important;
          }

          .forum-badge {
            width: 36px !important;
            height: 36px !important;
            font-size: 20px !important;
          }

          .forum-sign-name {
            font-size: 0.78rem !important;
            line-height: 1.05 !important;
            letter-spacing: 0 !important;
            text-align: center !important;
            padding-right: 0 !important;
          }

          .forum-sign-name[data-long="1"] {
            font-size: 0.68rem !important;
            letter-spacing: -0.25px !important;
          }

          .forum-sign-arrow {
            position: absolute !important;
            right: 50% !important;
            transform: translateX(50%) !important;
            bottom: 3px !important;
            font-size: 0.94rem !important;
          }

          .forum-back-btn {
            min-height: 38px !important;
            padding: 0 15px !important;
            font-size: 0.92rem !important;
          }

          .forum-notifs-btn {
            width: 42px !important;
            height: 42px !important;
            min-width: 42px !important;
            min-height: 42px !important;
            padding: 0 !important;
          }

          .forum-notifs-panel {
  position: absolute !important;
  top: calc(100% + 18px) !important;
  right: 0 !important;

  width: min(310px, calc(100vw - 58px)) !important;
  max-height: 220px !important;

  overflow: hidden !important;

  border-radius: 18px !important;
  border: 1px solid rgba(232,95,255,.46) !important;

  background: linear-gradient(
    180deg,
    rgba(13,3,31,.985),
    rgba(5,1,18,.992)
  ) !important;

  backdrop-filter: blur(18px) !important;
  -webkit-backdrop-filter: blur(18px) !important;

  box-shadow:
    0 22px 70px rgba(0,0,0,.6),
    0 0 28px rgba(190,60,255,.28) !important;

  z-index: 999999 !important;

  isolation: isolate;
  contain: paint;
}

          .forum-notifs-panel::before {
            content: "";
            position: absolute;
            top: -7px;
            right: 17px;
            width: 13px;
            height: 13px;
            transform: rotate(45deg);
            background: rgba(13,3,31,0.985);
            border-left: 1px solid rgba(232,95,255,0.46);
            border-top: 1px solid rgba(232,95,255,0.46);
            pointer-events: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          #forumHomeRoot::before,
          #forumHomeRoot::after,
          #forumHomeRoot .forumWheel,
          .forum-sign-item::after {
            animation: none !important;
          }
        }
      `}</style>

      <div id="forumHomeRoot" role="main" style={styles.page} aria-labelledby="forumHomeTitle">
        <div className="forumWheel" aria-hidden="true" />
        <div className="forum-card" style={styles.card}>
          <div className="forum-header" style={styles.header}>
            <button
              type="button"
              aria-label={t('home.actions.backToHome')}
              onClick={() => navigate('/home')}
              onMouseEnter={() => setHoverBack(true)}
              onMouseLeave={() => setHoverBack(false)}
              className="forum-back-btn"
              style={{ ...styles.backBtn, ...(hoverBack ? styles.backBtnHover : null) }}
            >
              <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '18px' }}>←</span>
              <span>{t('common:actions.back')}</span>
            </button>

            <div style={{ flex: 1, textAlign: 'center' }}>
              <h1 id="forumHomeTitle" className="forum-title" style={styles.title} title={t('home.title.full')}>
                {t('home.title.main')}<br />
                <span style={{ color: '#FFD979' }}>{t('home.title.bySign')}</span>
              </h1>
            </div>

            <div style={styles.headerRight}>
              <button
                id="forumNotifsBtn"
                type="button"
                aria-label={t('home.notifications.button')}
                onClick={() => {
                  const nextShow = !showNotifs;
                  setShowNotifs(nextShow);

                  if (!nextShow) return;

                  // Optimista: si abrimos el panel, marcar visualmente como leídas.
                  setUnreadNotifs(0);
                  setForumNotifs(prev => prev.map(x => ({ ...x, read: true })));

                  (async () => {
                    try {
                      setNotifsLoading(true);
                      const r = await forumApi.markForumNotificationsRead({ all: true });
                      if (r && r.ok) {
                        setUnreadNotifs(typeof r.unreadCount === 'number' ? r.unreadCount : 0);
                      } else {
                        setUnreadNotifs(0);
                      }
                    } catch {
                      setUnreadNotifs(0);
                    }

                    try {
                      const data = await forumApi.getForumNotifications({ limit: 12 });
                      if (data && data.ok) {
                        setForumNotifs(Array.isArray(data.items) ? data.items : []);
                      }
                    } catch {}
                    finally {
                      setNotifsLoading(false);
                    }
                  })();
                }}
                className="forum-notifs-btn"
                style={styles.notifsBtn}
              >
                <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '18px' }}>🔔</span>
                {!showNotifs && unreadNotifs > 0 && (
                  <span style={styles.notifsBadge} aria-label={t('home.notifications.unread', { count: unreadNotifs })}>
                    {unreadNotifs > 99 ? '99+' : unreadNotifs}
                  </span>
                )}
              </button>

              {showNotifs && (
                <div id="forumNotifsPanel" className="forum-notifs-panel" style={styles.notifsPanel} role="dialog" aria-label={t('home.notifications.title')}>
                  <div style={styles.notifsHeader}>
                    <div style={styles.notifsTitle}>{t('home.notifications.title')}</div>
                    <button
                      type="button"
                      onClick={() => setShowNotifs(false)}
                      aria-label={t('common:actions.close')}
                      style={styles.notifsClose}
                    >
                      ✕
                    </button>
                  </div>

                  {notifsLoading ? (
                    <div style={styles.notifsEmpty}>{t('common:states.loading')}</div>
                  ) : lastNotifs.length === 0 ? (
                    <div style={styles.notifsEmpty}>{t('home.notifications.empty')}</div>
                  ) : (
                    <div style={styles.notifsList}>
                      {lastNotifs.map((n) => (
                        <div
                          key={n.id}
                          role="button"
                          tabIndex={0}
                          style={styles.notifsItem}
                          onClick={async () => {
                            try {
                              // cerrar panel primero para UX
                              setShowNotifs(false);

                              // best-effort: marcar SOLO esta notif como leída
                              try {
                                const id = n?.id || n?._id;
                                if (id) {
                                  await forumApi.markForumNotificationsRead({ ids: [id] });

                                  // update optimista local
                                  setUnreadNotifs((c) => Math.max(0, Number(c || 0) - 1));
                                  setForumNotifs((prev) =>
                                    Array.isArray(prev)
                                      ? prev.map((x) => (String(x.id) === String(id) ? { ...x, read: true } : x))
                                      : prev
                                  );
                                }
                              } catch {}

                              // deep link al post exacto si existe
                              if (n?.threadId) {
                                const qs = n?.postId
                                  ? `?highlightPostId=${encodeURIComponent(String(n.postId))}`
                                  : '';
                                navigate(
                                  `/foro/tema/${encodeURIComponent(String(n.threadId))}${qs}`,
                                  { state: { fromNotifId: n.id } }
                                );
                                return;
                              }
                            } catch {}
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              // disparar click
                              e.currentTarget.click();
                            }
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <div style={{ flex: 1 }}>
                              <div style={styles.notifsMsg}>{n.message}</div>
                              <div style={styles.notifsMeta}>
                                {new Date(n.createdAt).toLocaleString('es-AR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                              </div>
                            </div>

                            <button
                              type="button"
                              aria-label={t('home.notifications.deleteAria')}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  if (typeof forumApi.archiveForumNotification === 'function') {
                                    await forumApi.archiveForumNotification(n.id);
                                  }
                                } catch {}

                                // UI optimista: quitar de la lista
                                setForumNotifs(prev => prev.filter(x => x.id !== n.id));
                              }}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 16,
                                opacity: 0.7
                              }}
                              title={t('home.notifications.delete')}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {err && (
            <div role="alert" aria-live="assertive" style={styles.error}>
              {err === FORUM_LOAD_ERROR ? t('home.errors.load') : err}
            </div>
          )}
          <div aria-hidden="true" style={styles.titleDivider}>
            <span />
            <i>✦</i>
            <span />
          </div>
          <p className="forum-subtitle" style={styles.subtitle}>
            {t('home.subtitle')}
          </p>

          <div className="forum-grid" style={styles.grid} role="list">
            {cats.map(({ key, name, emoji }) => {
              const zodiacTranslationKey = HOME_ZODIAC_KEY_BY_FORUM_SIGN[key];
              const displayName = zodiacTranslationKey
                ? t(`home:zodiacSigns.${zodiacTranslationKey}`)
                : name;

              return (
              <Link
                key={key}
                to={`/foro/${key}`}
                className="forum-sign-item"
                aria-label={t('home.category.open', { sign: displayName })}
                title={t('home.category.open', { sign: displayName })}
                role="listitem"
                onMouseEnter={() => setHoverItem(key)}
                onMouseLeave={() => setHoverItem(null)}
                style={{ ...styles.item, ...(hoverItem === key ? styles.itemHover : null) }}
              >
                <div className="forum-badge-wrap" style={styles.badgeWrap}>
                  <div className="forum-badge" style={{ ...styles.badge, ...(hoverItem === key ? styles.badgeHover : null) }}>{emoji}</div>
                </div>
                <div
                  className="forum-sign-name"
                  data-long={String(displayName || '').length >= 10 ? '1' : '0'}
                  style={{
                    ...styles.name,
                    ...(String(displayName || '').length >= 10
  ? { fontSize: 'clamp(11px, 3.2vw, 13.5px)', letterSpacing: '-0.2px' }
  : null),
                  }}
                >
                  {displayName}
                </div>
                <div className="forum-sign-arrow" style={styles.arrow} aria-hidden="true">→</div>
              </Link>
              );
            })}
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
}

const styles = {
  page: {
    minHeight: '100svh',
    padding: '74px 30px 34px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: '#05010F',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    isolation: 'isolate',
    color: '#fff',
  },
  card: {
  width: '100%',
  maxWidth: 980,
  position: 'relative',
  overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(20,5,42,0.09), rgba(8,2,24,0.16))',
    color: '#FFF6D8',
    borderRadius: 34,
    padding: '52px 38px 34px',
    boxShadow: '0 24px 70px rgba(0,0,0,0.38), 0 0 30px rgba(171,54,255,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
    border: '1px solid rgba(232,95,255,0.30)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    zIndex: 1,
    transform: 'translateY(-18px)',
  },
  header: {
  position: 'relative',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 10,
  paddingBottom: 0,
  background: 'transparent',
  paddingTop: 0,
  minHeight: 58,
},
  backBtn: {
    position: 'relative',
    marginRight: 'auto',
    zIndex: 2,
    minHeight: 44,
    padding: '0 19px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    border: '1px solid rgba(232,95,255,0.72)',
    background: 'rgba(14,4,31,0.72)',
    color: '#FFE08B',
    fontWeight: 900,
    borderRadius: 999,
    boxShadow: '0 0 22px rgba(190,60,255,0.32), inset 0 0 18px rgba(218,165,32,0.08)',
    transition: 'transform .12s ease, box-shadow .12s ease, filter .12s ease',
    fontSize: 15,
    cursor: 'pointer',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  backBtnHover: {
    transform: 'translateY(-1px) scale(1.02)',
    boxShadow: '0 0 28px rgba(190,60,255,0.42), inset 0 0 18px rgba(218,165,32,0.10)',
  },
  title: {
    margin: 0,
    fontSize: 'clamp(2.55rem, 7vw, 4.35rem)',
    fontWeight: 500,
    letterSpacing: '-0.035em',
    lineHeight: 0.98,
    color: '#F9F3FF',
    textShadow: '0 0 26px rgba(218,165,32,0.16)',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
  },
  titleDivider: {
    width: 'min(100%, 520px)',
    margin: '12px auto 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: '#FFD979',
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 660,
    margin: '0 auto 24px',
    color: 'rgba(255,255,255,0.84)',
    fontWeight: 500,
    fontSize: '1.08rem',
    lineHeight: 1.45,
  },
  error: { color: '#a00', marginTop: 8 },
  grid: {
    marginTop: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 14,
  },
  item: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: 150,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: '"badge" "name"',
    alignItems: 'center',
    justifyItems: 'center',
    gap: 8,
    textDecoration: 'none',
    background: 'linear-gradient(135deg, rgba(45,7,85,0.46), rgba(12,2,34,0.62))',
    border: '1px solid rgba(217,93,255,0.34)',
    borderRadius: 22,
    padding: '22px 14px 32px',
    color: '#FFF6D8',
    boxShadow: '0 12px 28px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.06)',
    transition: 'transform 150ms ease, box-shadow 200ms ease, border-color 200ms ease',
  },
  itemHover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 18px 38px rgba(0,0,0,0.34), 0 0 28px rgba(184,63,255,0.22)',
    filter: 'saturate(1.08)',
  },
  badgeWrap: {
    gridArea: 'badge',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    justifySelf: 'center',
    alignSelf: 'center',
    position: 'relative',
    zIndex: 2,
  },
  badge: {
    width: 58,
    height: 58,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at 35% 24%, rgba(190,80,255,0.52), rgba(35,6,70,0.84) 64%, rgba(5,1,18,0.92) 100%)',
    border: '1px solid rgba(255,218,116,0.72)',
    boxShadow: '0 0 22px rgba(190,80,255,0.38), inset 0 0 14px rgba(255,218,116,0.08)',
    fontSize: 34,
    color: '#FFD979',
    textShadow: '0 0 16px rgba(218,165,32,0.42)',
  },
  badgeHover: {
    transform: 'scale(1.06)',
    boxShadow: '0 0 28px rgba(218,165,32,0.38), 0 0 26px rgba(190,80,255,0.46)',
  },
  arrow: {
    position: 'absolute',
    right: 18,
    bottom: 13,
    fontWeight: 900,
    fontSize: '1.35rem',
    color: '#FFD979',
    textShadow: '0 0 12px rgba(218,165,32,0.48)',
    lineHeight: 1,
    zIndex: 2,
  },
  emoji: { display:'none' },
  name: {
    gridArea: 'name',
    position: 'relative',
    zIndex: 2,
    fontWeight: 800,
    color: '#FFFFFF',
    fontSize: '1.08rem',
    lineHeight: 1.18,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'unset',
    paddingRight: 2,
    textShadow: '0 2px 12px rgba(0,0,0,0.45)',
  },
  headerRight: {
  position: 'relative',
  zIndex: 1001,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
},
  notifsBtn: {
    position: 'relative',
    width: 50,
    height: 50,
    minWidth: 50,
    minHeight: 50,
    padding: 0,
    borderRadius: '50%',
    border: '1px solid rgba(255,218,116,0.78)',
    background: 'rgba(8,2,24,0.54)',
    color: '#FFD979',
    boxShadow: '0 0 24px rgba(218,165,32,0.22), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 900,
    overflow: 'hidden',
    lineHeight: 1,
  },
  notifsBadge: {
    position: 'absolute',
    top: -5,
    right: -5,
    background: 'linear-gradient(135deg, #DAA520, #FFD54F)',
    color: '#2a0051',
    border: '1px solid rgba(5,1,15,0.28)',
    borderRadius: 999,
    padding: '2px 6px',
    fontSize: 12,
    fontWeight: 900,
    boxShadow: '0 6px 16px rgba(0,0,0,0.28)',
  },
  notifsPanel: {
  position: 'absolute',
  top: 'calc(100% + 18px)',
  right: 0,

  width: 'min(360px, calc(100vw - 24px))',
  maxHeight: 'min(60vh, 520px)',

  overflow: 'hidden',

  borderRadius: 18,
  border: '1px solid rgba(232,95,255,0.42)',

  background:
    'linear-gradient(180deg, rgba(13,3,31,0.985), rgba(5,1,18,0.992))',

  boxShadow:
    '0 24px 80px rgba(0,0,0,0.65), 0 0 30px rgba(190,60,255,0.28)',

  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',

  isolation: 'isolate',
  contain: 'paint',

  zIndex: 999999,
},
  notifsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(75,0,130,0.10)',
    background: 'linear-gradient(135deg, rgba(255,224,130,0.10), rgba(188,69,255,0.08))',
  },
  notifsTitle: {
    fontWeight: 900,
    color: '#FFF6D8',
    letterSpacing: 0.2,
  },
  notifsClose: {
    border: '1px solid rgba(255,218,116,0.20)',
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 999,
    padding: '6px 10px',
    cursor: 'pointer',
    fontWeight: 900,
    color: '#FFD979',
  },
  notifsEmpty: {
    padding: '14px 13px',
    color: 'rgba(255,255,255,0.88)',
    opacity: 1,
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1.35,
    textAlign: 'left',
  },
  notifsList: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflowY: 'auto',
    maxHeight: 'calc(min(60vh, 520px) - 52px)'
  },
  notifsItem: {
    textAlign: 'left',
    width: '100%',
    border: '1px solid rgba(232,95,255,0.20)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.075), rgba(255,255,255,0.035))',
    borderRadius: 14,
    padding: '9px 10px',
    cursor: 'pointer',
    boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
  },
  notifsMsg: {
    fontWeight: 900,
    color: '#FFF6D8',
    letterSpacing: 0.15,
    fontSize: 13,
    lineHeight: 1.25,
    marginBottom: 4,
  },
  notifsMeta: {
    fontSize: 11,
    color: '#C7B8FF',
    opacity: 0.82,
    fontWeight: 700,
  },
};
